import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, TextField, InputAdornment, Box,
  Avatar, List, ListItem, ListItemText, Pagination, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import './PresentationModal.css';

const PresentationModal = ({ open, onClose, talent, apiBase }) => {
  const [missions, setMissions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [usedMissionMap, setUsedMissionMap] = useState({});
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const [errorMsg, setErrorMsg] = useState('');
  const itemsPerPage = 5;
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchMissions = async () => {
      try {
        const res = await axios.get(`${apiBase}/api/missions/active`);
        const sorted = res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
        if (!talent?._id) {
          setMissions(sorted);
          setFiltered(sorted);
          return;
        }
    
        // Fetch similarity score for each mission
        const missionsWithSimilarity = await Promise.all(
          sorted.map(async (mission) => {
            try {
              const simRes = await axios.get(`${API_BASE}/api/matching/similarity/${mission._id}/${talent._id}`);
              return { ...mission, similarity: simRes.data.similarity || 0 };
            } catch (err) {
              console.error(`Erreur similarité pour mission ${mission._id}`, err);
              return { ...mission, similarity: 0 };
            }
          })
        );
    
        setMissions(missionsWithSimilarity);
        setFiltered(missionsWithSimilarity);
      } catch (err) {
        console.error('Erreur chargement missions :', err);
      }
    };
    

    const fetchUsedMissions = async () => {
      if (!talent?._id) return;
      try {
        const res = await axios.get(`${apiBase}/api/candidatures/envoyees`, {
          params: { talentId: talent._id }
        });
        const map = {};
        res.data.forEach(({ missionId, statut }) => {
          map[missionId] = statut;
        });
        setUsedMissionMap(map);
      } catch (err) {
        console.error('Erreur chargement missions ou candidatures :', err);
      }
    };

    if (open && talent?._id) {
      fetchMissions();
      fetchUsedMissions();
      setSelected(null);
      setErrorMsg('');
    }
  }, [open, talent, apiBase]);

  const handleFilter = (val) => {
    setSearch(val);
    const lower = val.toLowerCase();
    const filteredList = missions.filter(m =>
      (m.nomMission || '').toLowerCase().includes(lower)
    );
    setFiltered(filteredList);
    setPage(1);
  };

  const handleSubmit = () => {
    if (!selected || !talent?._id) return;

    axios.post(`${apiBase}/api/candidatures/submit`, {
      talentId: talent._id,
      missionId: selected._id
    }).then(() => {
      onClose();
      window.open(`/recruteur/travailler-mission/${selected._id}?t=${talent._id}`, '_blank');
    }).catch(err => {
      console.error('Erreur envoi candidature', err.response?.data || err.message);
      if (err.response?.status === 409) {
        setErrorMsg("Ce talent a déjà été présenté sur cette mission.");
        setSelected(null);
      }
    });
  };

  const handleCreateDraft = async () => {
    if (!selected || !talent?._id) return;

    try {
      await axios.post(`${apiBase}/api/candidatures/submit`, {
        talentId: talent._id,
        missionId: selected._id
      });
      onClose(); // reste sur le talent
    } catch (err) {
      console.error('Erreur création brouillon', err.response?.data || err.message);
      setErrorMsg("Impossible de créer le brouillon. Veuillez réessayer.");
    }
  };

  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="presentation-title">
        Présenter {talent?.prenom} {talent?.nom} sur une de mes missions
      </DialogTitle>
      <DialogContent dividers>
        <Typography className="presentation-intro">
          Vous pouvez présenter ce talent sur une de vos missions.<br />
          <strong>Cette action créera un brouillon de candidature</strong> que vous devrez compléter.
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ my: 2 }}>{errorMsg}</Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher une mission"
          value={search}
          onChange={(e) => handleFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          className="presentation-search"
        />

        <List>
          {paginated.map((mission) => {
            const statut = usedMissionMap[mission._id];
            const isUsed = !!statut;
            const isDraft = statut?.toLowerCase() === 'brouillon';
            const isSelected = selected?._id === mission._id;
            const isDimmed = selected && !isSelected;

            return (
              <ListItem
                key={mission._id}
                className={`mission-item ${isSelected ? 'selected' : ''} ${isDimmed ? 'dimmed' : ''} ${isUsed && !isDraft ? 'used-mission' : ''}`}
                onClick={() => {
                  if (!isUsed || isDraft) setSelected(mission);
                }}
                disabled={isUsed && !isDraft}
              >
                <ListItemText
                  primary={
                    <>
                      <Typography className="mission-title">{mission.nomMission || 'Sans titre'}</Typography>
                      {isDraft && <Typography variant="caption" color="warning.main">Brouillon en cours</Typography>}
                      {isUsed && !isDraft && <Typography variant="caption" color="error">Déjà présenté</Typography>}
                    </>
                  }
                  secondary={
                    <>
                      <Typography className="mission-sub" component="span">
                        {mission.lieuxTravail || 'Lieu inconnu'}
                      </Typography>
                      <Typography
                          variant="body2"
                          color={(mission.similarity * 100) > 60 ? 'success.main' : 'error.main'}
                        >
                          Similarité : {(mission.similarity * 100).toFixed(2)}%
                        </Typography>
                    </>
                  }
                />

                <Box className="mission-right">
                  <Typography className="mission-company">{mission.entreprise?.nom || 'Entreprise inconnue'}</Typography>
                  <Avatar src={mission.entreprise?.logoUrl} className="mission-avatar">
                    {mission.entreprise?.nom?.[0] || '?'}
                  </Avatar>
                  {isDraft && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>Brouillon</Typography>
                  )}
                  {isUsed && !isDraft && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>Déjà présenté</Typography>
                  )}
                  {isSelected && (!isUsed || isDraft) && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/Recruteur/Details/${mission._id}`, '_blank');
                        }}
                        className="details-button"
                      >
                        Détails
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(null);
                        }}
                        className="unselect-button"
                      >
                        ✕
                      </Button>
                    </Box>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>

        {filtered.length > itemsPerPage && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filtered.length / itemsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>

        {selected && (
          <>
            {usedMissionMap[selected._id]?.toLowerCase() === 'brouillon' ? (
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  onClose();
                  window.open(`/recruteur/travailler-mission/${selected._id}?t=${talent._id}`, '_blank');
                }}
              >
                Reprendre le brouillon
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCreateDraft}
                >
                  Créer le brouillon et rester sur le talent
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                >
                  Créer et compléter la candidature
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PresentationModal;
